import { Api } from '@square/ignition';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReferenceHrefBuilders } from '@squareup/dex-tech-utils-routing';
import { Box } from '@squareup/dex-ui-shared-base';
import {
  MarketInputSearch,
  MarketList,
  MarketRow,
  MarketSelect,
} from '@squareup/dex-ui-shared-market';
import {
  filterForValidApiExplorerEndpoints,
  toApiId,
  toEndpointId,
} from '@squareup/dex-utils-oas';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useRef } from 'react';

import styles from './api-explorer-api-picker.module.css';

const strings = {
  selectLabel: 'API',
  selectPlaceholder: 'Select API',
  searchPlaceholder: 'Search APIs',
};

interface Props {
  apis: Api[] | undefined;
  selectedApi?: Api | undefined;
  currentVersion: string;
}

const ApiExplorerApiPicker: React.FC<Props> = memo(
  ({ apis, selectedApi, currentVersion }) => {
    const router = useRouter();

    const apiList = apis
      ?.filter(
        (api) => filterForValidApiExplorerEndpoints(api.endpoints).length > 0
      )
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    const inputSearchRef =
      useRef<globalThis.HTMLMarketInputSearchElement>(null);

    const onOpen = useCallback(() => {
      inputSearchRef.current?.setFocus();
    }, []);

    const onClose = useCallback(() => {
      inputSearchRef.current?.clearInput();
    }, []);

    return (
      <Box
        id="api-picker-select-container"
        className={styles['api-picker-container']}
      >
        <MarketSelect
          data-testid="api-explorer-api-picker-select"
          placeholder={strings.selectPlaceholder}
          size="small"
          value={selectedApi?.id || ''}
          onMarketSelectOpened={onOpen}
          onMarketSelectClosed={onClose}
          popoverContainer="#api-picker-select-container"
        >
          <label>{strings.selectLabel}</label>
          <MarketList slot="list" className={clsx(styles.list)}>
            {apiList && apiList.length > 1 && (
              <MarketInputSearch
                slot="search"
                size="small"
                ref={inputSearchRef}
                placeholder={strings.searchPlaceholder}
                className={clsx(styles['search-input'])}
              />
            )}
            {apiList?.map((api) => {
              const endpointId =
                filterForValidApiExplorerEndpoints(api.endpoints)[0]?.id ?? '';

              const href = ReferenceHrefBuilders.versionSquareExplorerEndpoint({
                api: toApiId(api?.id),
                endpoint: toEndpointId(endpointId),
                square_version: currentVersion,
                ...('env' in router.query && {
                  env: router.query['env'] as 'sandbox' | 'production',
                }),
                ...('prefill' in router.query && {
                  prefill: router.query['prefill'] as string,
                }),
                ...('appId' in router.query && {
                  appId: router.query['appId'] as string,
                }),
              });

              return (
                <MarketRow
                  size="small"
                  onMarketRowSelected={() => {
                    router.push(href);
                  }}
                  key={api.id}
                  value={api.id}
                >
                  <Box as="label" slot="label">
                    {api.name}
                  </Box>
                </MarketRow>
              );
            })}
          </MarketList>
        </MarketSelect>
      </Box>
    );
  }
);

ApiExplorerApiPicker.displayName = 'ApiExplorerApiPicker';

export { ApiExplorerApiPicker };
